/**
 * Integrytas Group - Common Security Module (CSM)
 * About modal — app version, current page version, licence, platform info.
 * Triggered by the (i) icon in the header or pre-auth screens.
 */

import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PAGE_REGISTRY } from '../../pages/page_registry';
import { apiDb } from '../../lib/api_client';
import { asfWorkspaceCode } from '../../lib/asf';
import { describeDeltaMap, type UldChangeLine } from '../../lib/uld_describe';
import type { UldDeltaAction } from '../../lib/uld_merge';
import type { UldTier } from '../../hooks/use_uld_layout';
import { useFormatDate } from '../../lib/format_date';

const APP_VERSION = ((import.meta.env.VITE_APP_VERSION as string | undefined) ?? '').trim() || '—';
const ENV_LABEL   = ((import.meta.env.VITE_ENV_LABEL  as string | undefined) ?? '').trim()
                    || (import.meta.env.DEV ? 'DEV' : 'PROD');
const IS_DEV      = import.meta.env.DEV;

interface LicStatus {
  licensing_enabled: boolean;
  tenant: { app: string; customer: string } | null;
  available: boolean;
  modules: string[];
  catalog: string[];
  expires_at: string | null;
}

export interface AboutModalProps {
  open: boolean;
  onClose: () => void;
  activePageCode?: string | null;
  appName?: string;
  isAuthenticated?: boolean;
}

type Tab = 'about' | 'licence' | 'platform';

const ROW = 'flex items-start gap-3 py-1.5 border-b border-csm-border last:border-0';
const LBL = 'w-40 shrink-0 text-csm-sm text-csm-muted';
const VAL = 'text-csm-sm text-csm-ink font-medium';

interface PageVersionInfo {
  version_number: string | null;
  release_version_number: string | null;
  release_date: string | null;
}

// Personalization tiers shown (lowest → highest precedence) in the breakdown.
const PERSO_TIERS: UldTier[] = ['SYSTEM', 'ROLE', 'USER'];
const TIER_LABEL_KEY: Record<UldTier, string> = {
  SYSTEM: 'about.tier_system', ROLE: 'about.tier_role', USER: 'about.tier_user',
};

export function AboutModal({ open, onClose, activePageCode, appName = 'APPLICATION', isAuthenticated = false }: AboutModalProps) {
  const { t } = useTranslation();
  const formatDate = useFormatDate();
  const [tab, setTab]       = useState<Tab>('about');
  const [lic, setLic]       = useState<LicStatus | null>(null);
  const [licBusy, setLicBusy] = useState(false);
  const [pageInfo, setPageInfo] = useState<PageVersionInfo | null>(null);
  // Per-tier personalization breakdown for the current page (collapsed by default,
  // lazily fetched on first expand).
  const [persoOpen, setPersoOpen] = useState(false);
  const [perso, setPerso] = useState<Record<UldTier, UldChangeLine[]> | null>(null);
  const [persoBusy, setPersoBusy] = useState(false);
  const backdropRef = useRef<HTMLDivElement>(null);

  // internal_code from static registry (always available for pre-auth screens)
  const pageDef = activePageCode
    ? PAGE_REGISTRY.find(p => p.internal_code === activePageCode)
    : undefined;

  // Fetch live version info from DB when authenticated
  useEffect(() => {
    if (!open || !isAuthenticated || !activePageCode) { setPageInfo(null); return; }
    void (async () => {
      try {
        const { data } = await apiDb
          .from('csm_page_master')
          .select('version_number, release_version_number, release_date')
          .eq('internal_code', activePageCode)
          .single();
        if (data) setPageInfo(data as PageVersionInfo);
      } catch { /* noop */ }
    })();
  }, [open, isAuthenticated, activePageCode]);

  // Reset the breakdown whenever the active page changes (so a re-open on a
  // different page re-fetches rather than showing stale rows).
  useEffect(() => { setPerso(null); setPersoOpen(false); }, [activePageCode]);

  // Lazily load the per-tier deltas the first time the section is expanded.
  useEffect(() => {
    if (!persoOpen || !open || !isAuthenticated || !activePageCode || perso || persoBusy) return;
    setPersoBusy(true);
    void (async () => {
      try {
        const ws = asfWorkspaceCode();
        const results = await Promise.all(PERSO_TIERS.map(tier =>
          apiDb.rpc<Record<string, UldDeltaAction[]>>('uld_my_delta', {
            p_page_code: activePageCode, p_workspace_code: ws, p_scope_type: tier,
          })));
        const next = { SYSTEM: [], ROLE: [], USER: [] } as Record<UldTier, UldChangeLine[]>;
        PERSO_TIERS.forEach((tier, i) => { next[tier] = describeDeltaMap(results[i]?.data); });
        setPerso(next);
      } catch {
        setPerso({ SYSTEM: [], ROLE: [], USER: [] });
      } finally {
        setPersoBusy(false);
      }
    })();
  }, [persoOpen, open, isAuthenticated, activePageCode, perso, persoBusy]);

  useEffect(() => {
    if (!open) { setTab('about'); setPageInfo(null); setPersoOpen(false); return; }
    if (!isAuthenticated || lic || licBusy) return;
    setLicBusy(true);
    void (async () => {
      try {
        const { data } = await apiDb.licStatus<LicStatus>();
        if (data) setLic(data);
      } catch { /* unavailable */ }
      finally { setLicBusy(false); }
    })();
    // lic/licBusy are read only as a fetch-once guard; adding them as deps would
    // re-trigger the effect as licBusy toggles and defeat the single-fetch intent.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, isAuthenticated]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  const persoTotal = perso ? perso.SYSTEM.length + perso.ROLE.length + perso.USER.length : 0;

  const tabs: { id: Tab; label: string }[] = [
    { id: 'about',   label: t('about.tab_about')   },
    { id: 'licence', label: t('about.tab_licence')  },
    { id: 'platform',label: t('about.tab_platform') },
  ];

  return (
    <div
      ref={backdropRef}
      className="fixed inset-0 z-[110] flex items-center justify-center bg-black/40"
      onMouseDown={e => { if (e.target === backdropRef.current) onClose(); }}
    >
      <div className="w-full max-w-md rounded border border-csm-border bg-white shadow-2xl flex flex-col"
           style={{ maxHeight: '90vh' }}>

        {/* Header */}
        <header className="flex items-center justify-between border-b border-csm-border px-4 py-2 bg-primary rounded-t">
          <h2 className="text-csm-md font-semibold text-white flex items-center gap-2">
            <span className="text-white/80">ℹ</span>
            {t('about.title', { app: appName })}
          </h2>
          <button type="button" onClick={onClose}
                  className="text-white/70 hover:text-white text-lg leading-none px-1"
                  aria-label={t('common.close')}>✕</button>
        </header>

        {/* Tab strip */}
        <div className="flex border-b border-csm-border bg-csm-surface-alt shrink-0">
          {tabs.map(({ id, label }) => (
            <button key={id} type="button"
                    onClick={() => setTab(id)}
                    className={[
                      'px-4 py-2 text-csm-sm font-medium border-b-2 -mb-px transition-colors',
                      tab === id
                        ? 'border-primary text-primary'
                        : 'border-transparent text-csm-muted hover:text-csm-ink',
                    ].join(' ')}>
              {label}
            </button>
          ))}
        </div>

        {/* Body */}
        <div className="overflow-y-auto flex-1 px-4 py-3">

          {/* ── About ── */}
          {tab === 'about' && (
            <div className="space-y-4">
              <section>
                <h3 className="mb-2 text-csm-xs font-semibold uppercase tracking-wide text-csm-muted">
                  {t('about.section_application')}
                </h3>
                <div className="divide-y divide-csm-border rounded border border-csm-border px-3">
                  <div className={ROW}><span className={LBL}>{t('about.field_name')}</span><span className={VAL}>{appName}</span></div>
                  <div className={ROW}><span className={LBL}>{t('about.field_version')}</span><span className={VAL}>{APP_VERSION}</span></div>
                  <div className={ROW}><span className={LBL}>{t('about.field_environment')}</span>
                    <span className={[VAL, 'font-mono'].join(' ')}>{ENV_LABEL}</span>
                  </div>
                </div>
              </section>

              {pageDef && (
                <section>
                  <h3 className="mb-2 text-csm-xs font-semibold uppercase tracking-wide text-csm-muted">
                    {t('about.section_current_page')}
                  </h3>
                  <div className="divide-y divide-csm-border rounded border border-csm-border px-3">
                    <div className={ROW}>
                      <span className={LBL}>{t('about.field_page')}</span>
                      <span className={[VAL, 'font-mono text-csm-xs'].join(' ')}>{pageDef.internal_code}</span>
                    </div>
                    <div className={ROW}>
                      <span className={LBL}>{t('about.field_release_version')}</span>
                      <span className={VAL}>
                        {(pageInfo?.release_version_number ?? pageDef.release_version_number) ?? '—'}
                      </span>
                    </div>
                    <div className={ROW}>
                      <span className={LBL}>{t('about.field_release_date')}</span>
                      <span className={VAL}>
                        {formatDate(pageInfo?.release_date ?? pageDef.release_date) || '—'}
                      </span>
                    </div>
                    {IS_DEV && (
                      <div className={ROW}>
                        <span className={LBL}>{t('about.field_component_version')}</span>
                        <span className={[VAL, 'text-amber-600'].join(' ')}>
                          {(pageInfo?.version_number ?? pageDef.version_number) ?? '—'}
                          <span className="ml-1.5 text-csm-2xs font-normal text-csm-muted">(dev only)</span>
                        </span>
                      </div>
                    )}
                  </div>
                </section>
              )}

              {isAuthenticated && activePageCode && (
                <section>
                  <button
                    type="button"
                    onClick={() => setPersoOpen(o => !o)}
                    aria-expanded={persoOpen}
                    className="mb-2 flex w-full items-center gap-1.5 text-csm-xs font-semibold uppercase tracking-wide text-csm-muted hover:text-csm-ink"
                  >
                    <span className="inline-block w-3 text-csm-ink">{persoOpen ? '▾' : '▸'}</span>
                    {t('about.section_personalization')}
                    {perso && <span className="font-normal normal-case text-csm-muted">({persoTotal})</span>}
                  </button>
                  {persoOpen && (
                    persoBusy ? (
                      <p className="py-2 text-csm-sm italic text-csm-muted">{t('common.loading')}</p>
                    ) : persoTotal === 0 ? (
                      <p className="py-1 text-csm-sm italic text-csm-muted">{t('about.personalization_none')}</p>
                    ) : (
                      <div className="space-y-2 rounded border border-csm-border px-3 py-2">
                        {PERSO_TIERS.map(tier => (
                          <div key={tier}>
                            <div className="text-csm-2xs font-semibold uppercase tracking-wide text-csm-muted">
                              {t(TIER_LABEL_KEY[tier])}
                            </div>
                            {perso && perso[tier].length > 0 ? (
                              <ul className="ml-1 list-inside list-disc text-csm-sm text-csm-ink">
                                {perso[tier].map((line, i) => (
                                  <li key={i}>
                                    {line.component_key && (
                                      <span className="font-mono text-csm-2xs text-csm-muted">{line.component_key}: </span>
                                    )}
                                    {line.text}
                                  </li>
                                ))}
                              </ul>
                            ) : (
                              <p className="text-csm-sm italic text-csm-muted">{t('about.personalization_tier_none')}</p>
                            )}
                          </div>
                        ))}
                      </div>
                    )
                  )}
                </section>
              )}
            </div>
          )}

          {/* ── Licence ── */}
          {tab === 'licence' && (
            <div>
              {!isAuthenticated ? (
                <p className="text-csm-sm text-csm-muted italic py-4 text-center">
                  {t('about.licence_requires_auth')}
                </p>
              ) : licBusy ? (
                <p className="text-csm-sm text-csm-muted italic py-4 text-center">{t('common.loading')}</p>
              ) : !lic ? (
                <p className="text-csm-sm text-csm-muted italic py-4 text-center">{t('about.licence_unavailable')}</p>
              ) : (
                <div className="space-y-4">
                  <div className="divide-y divide-csm-border rounded border border-csm-border px-3">
                    <div className={ROW}>
                      <span className={LBL}>{t('about.field_licensing')}</span>
                      <span className={VAL}>{lic.licensing_enabled ? t('about.lic_enforced') : t('about.lic_open')}</span>
                    </div>
                    {lic.tenant && <>
                      <div className={ROW}><span className={LBL}>{t('about.field_application')}</span><span className={VAL}>{lic.tenant.app}</span></div>
                      <div className={ROW}><span className={LBL}>{t('about.field_customer')}</span><span className={VAL}>{lic.tenant.customer}</span></div>
                    </>}
                    <div className={ROW}>
                      <span className={LBL}>{t('about.field_expires')}</span>
                      <span className={VAL}>{lic.expires_at ? formatDate(lic.expires_at) : '—'}</span>
                    </div>
                  </div>

                  {lic.modules.length > 0 && (
                    <section>
                      <h3 className="mb-2 text-csm-xs font-semibold uppercase tracking-wide text-csm-muted">
                        {t('about.section_entitled_modules')}
                      </h3>
                      <div className="flex flex-wrap gap-1.5">
                        {lic.modules.map(m => (
                          <span key={m} className="rounded border border-green-200 bg-green-50 px-2 py-0.5 text-csm-xs font-mono text-green-700">{m}</span>
                        ))}
                      </div>
                    </section>
                  )}

                  {lic.catalog.filter(m => !lic.modules.includes(m)).length > 0 && (
                    <section>
                      <h3 className="mb-2 text-csm-xs font-semibold uppercase tracking-wide text-csm-muted">
                        {t('about.section_available_modules')}
                      </h3>
                      <div className="flex flex-wrap gap-1.5">
                        {lic.catalog.filter(m => !lic.modules.includes(m)).map(m => (
                          <span key={m} className="rounded border border-csm-border bg-csm-surface-alt px-2 py-0.5 text-csm-xs font-mono text-csm-muted">{m}</span>
                        ))}
                      </div>
                    </section>
                  )}
                </div>
              )}
            </div>
          )}

          {/* ── Platform ── */}
          {tab === 'platform' && (
            <div className="space-y-4">
              <section>
                <h3 className="mb-2 text-csm-xs font-semibold uppercase tracking-wide text-csm-muted">
                  {t('about.section_frontend')}
                </h3>
                <div className="divide-y divide-csm-border rounded border border-csm-border px-3">
                  {[
                    ['Runtime',  'React 18'],
                    ['Language', 'TypeScript'],
                    ['Build',    'Vite 8 + Rolldown'],
                    ['Styling',  'Tailwind CSS'],
                    ['i18n',     'i18next'],
                  ].map(([k, v]) => (
                    <div key={k} className={ROW}><span className={LBL}>{k}</span><span className={VAL}>{v}</span></div>
                  ))}
                </div>
              </section>
              <section>
                <h3 className="mb-2 text-csm-xs font-semibold uppercase tracking-wide text-csm-muted">
                  {t('about.section_backend')}
                </h3>
                <div className="divide-y divide-csm-border rounded border border-csm-border px-3">
                  {[
                    ['Database',  'PostgreSQL 15'],
                    ['Auth',      'GoTrue (Supabase)'],
                    ['API',       'PostgREST 14'],
                    ['Functions', 'Deno Edge Runtime'],
                    ['Gateway',   'Kong 3'],
                    ['Storage',   'Supabase Storage'],
                  ].map(([k, v]) => (
                    <div key={k} className={ROW}><span className={LBL}>{k}</span><span className={VAL}>{v}</span></div>
                  ))}
                </div>
              </section>
              <p className="text-center text-csm-xs text-csm-muted pt-1">
                Integrytas Platform · Integrytas Group
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <footer className="flex justify-end border-t border-csm-border px-4 py-2 shrink-0">
          <button type="button" onClick={onClose}
                  className="h-7 rounded border border-csm-border bg-white px-4 text-csm-sm text-csm-ink hover:bg-csm-surface-alt">
            {t('common.close')}
          </button>
        </footer>
      </div>
    </div>
  );
}
